<template>
    <Rows-container 
        ref="rowsContainer" 
        class="details-lines"
        :items-props="partLines"
        @on-down="onDownRows"
    >
        <template v-for="(item, index) in partLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                @loaded-line="onLoadedLine"
                @list-item-selected="itemsSelected"
            />
        </template>
    </Rows-container>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, watch, nextTick } from "vue";
    import { AppConstants } from "@/common";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useLinesDetailProgramStore } from "@/stores/LinesDetailProgram";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import { storeToRefs } from 'pinia';
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import { useParentControlStore } from "@/stores/ParentControl";
    import type { ExposeInterface } from "./types";
    import { useDetailProgramPageStore } from "@/stores/DetailProgramPage";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";


    const DetailProgramPage = useDetailProgramPageStore();
    const {
        getCurrentChannel: currentChannel
    } = storeToRefs(DetailProgramPage);

    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);

    const LinesDetailProgramStore = useLinesDetailProgramStore();
    const {
        getCounterLinesDetailProgram,
        getCardsProgram
    } = storeToRefs(LinesDetailProgramStore);

    const { increaseCounterLinesDetailProgram } = LinesDetailProgramStore;

    const emit = defineEmits(['show-all', 'list-item-selected', 'ready']);
    const selectedItem: Ref<IEventSelected | null> = ref(null);

    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const name = "LinesDetailProgram";
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const getProgramsTitle = computed((): string => currentChannel.value?.name ?? "");
    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            {
                keyData: shelfKeys.programsCards,
                title: getProgramsTitle.value,
                ref: shelfKeys.programsCards,
                lineId: shelfKeys.programsCards,
                skeleton: SkeletonName.PROGRAM_CARD_SKELETON,
                cardItems: getCardsProgram.value,
            },
        ];
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesDetailProgram.value));

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };
    
    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !== null && getCounterLinesDetailProgram.value >= countLoadedRows) {
            void nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= AppConstants.HomeCounterLines) {
            const isIncreasing = (counter.value % AppConstants.HomeCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesDetailProgram();
                counter.value++;
            }
        }
    };

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        }).catch((error)=> {
            Logger.captureError(error)
        });
    };

    const onDownRows = (): void => {
        if (getCounterLinesDetailProgram.value < allLines.value.length) {
            increaseCounterLinesDetailProgram();
        }
    };

    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>